import {Kysely, sql} from 'kysely';
import {ModuleAPI} from 'springboard/engine/module_api';
import {DBObjects} from '../store_types';

import '../../modules/user_context/user_context_module';
import {z} from 'zod';
import {createZodAction} from '../actions/actions';
import {AllModules} from 'springboard/module_registry/module_registry';
import {AttachmentChildType, AttachmentParentType, AttributeName, DB, UserPreferenceType} from '../kysely_types';

type WorkspaceOptions = {
    workspace_id: string;
}

export class Selectors {
    constructor(private moduleAPI: ModuleAPI) { }

    private getDB = () => {
        return this.moduleAPI.deps.module.moduleRegistry.getModule('Kysely').db();
    }

    public getAvatarFileInfo = createZodAction(z.object({
        file_id: z.string(),
    }), async (args) => {
        const db = this.getDB();

        const userContext = await getCurrentUserContext(this.moduleAPI);
        if (!userContext) {
            throw new Error('Not logged in');
        }

        const file = await db.selectFrom('File').selectAll()
            .where('File.id', '=', args.file_id)
            .executeTakeFirst();

        if (!file) {
            return null;
        }

        return file;
    });

    public getFileInfo = createZodAction(z.object({
        file_id: z.string(),
    }), async (args) => {
        const db = this.getDB();

        const userContext = await getCurrentUserContext(this.moduleAPI);
        if (!userContext) {
            throw new Error('Not logged in');
        }

        const file = await db.selectFrom('File').selectAll()
            .where('File.id', '=', args.file_id)
            .executeTakeFirst();

        const rawFiles = await db.selectFrom('RawFile')
            .where('RawFile.file_id', '=', args.file_id)
            .selectAll()
            .execute();

        if (!file) {
            return null;
        }

        if (!await checkWorkspaceMembership(this.moduleAPI, file)) {
            throw new Error('Not a member of workspace');
        }

        const attributeRows = await db.selectFrom('Attribute')
            .where('Attribute.parent_id', '=', args.file_id)
            .where('Attribute.parent_type', '=', AttachmentParentType.File)
            .selectAll()
            .execute();

        const attributes = {
            [AttributeName.description]: attributeRows.find(a => a.name === AttributeName.description)?.value,
            [AttributeName.chord_lyrics]: attributeRows.find(a => a.name === AttributeName.chord_lyrics)?.value,
            [AttributeName.trim_metadata]: attributeRows.find(a => a.name === AttributeName.trim_metadata)?.value,
            [AttributeName.duration]: attributeRows.find(a => a.name === AttributeName.duration)?.value,
        };

        const isStarred = await db.selectFrom('Star')
            .select('id')
            .where('parent_id', '=', args.file_id)
            .where('parent_type', '=', 'File')
            .where('created_by', '=', userContext.id)
            .executeTakeFirst();

        const projectAttachment = await db.selectFrom('Attachment')
            .select(['parent_id'])
            .where('child_id', '=', args.file_id)
            .where('child_type', '=', AttachmentChildType.File)
            .where('parent_type', '=', AttachmentParentType.Project)
            .executeTakeFirst();

        const latestFileRevision = await db.selectFrom('FileRevision')
            .select('id')
            .where('file_id', '=', args.file_id)
            .orderBy('revision_num', 'desc')
            .executeTakeFirst();

        const isPublishedToProfile = latestFileRevision ? await db.selectFrom('ProfilePublicTrack')
            .select('id')
            .where('user_id', '=', userContext.id)
            .where('file_revision_id', '=', latestFileRevision.id)
            .executeTakeFirst() : null;

        return {
            ...file,
            attributes,
            isStarred: Boolean(isStarred),
            raw_files: rawFiles,
            projectId: projectAttachment?.parent_id,
            file_revision_id: latestFileRevision?.id,
            isPublishedToProfile: Boolean(isPublishedToProfile),
        };
    });

    public getAllMyStars = createZodAction(z.object({}), async () => {
        const db = this.getDB();
        const userContext = await getCurrentUserContext(this.moduleAPI);
        if (!userContext) {
            throw new Error('Not logged in');
        }

        const starredObjects = await db.selectFrom('Star')
            .selectAll()
            .where('created_by', '=', userContext.id)
            .execute();

        const fileIds = starredObjects.filter(star => star.parent_type === 'File').map(star => star.parent_id);
        const files = await db.selectFrom('File')
            .innerJoin('Attribute', 'Attribute.parent_id', 'File.id')
            .where('File.id', 'in', fileIds)
            .where('Attribute.name', '=', AttributeName.duration)
            .selectAll('File')
            .select('Attribute.value as duration')
            .execute();

        const projectIds = starredObjects.filter(star => star.parent_type === 'Project').map(star => star.parent_id);
        const projects = await db.selectFrom('Project')
            .where('Project.id', 'in', projectIds)
            .selectAll()
            .execute();

        const projectAttributes = projectIds.length === 0 ? [] :
            await db.selectFrom('Attribute')
                .select(['parent_id', 'name', 'value'])
                .where('parent_type', '=', 'Project')
                .where('parent_id', 'in', projectIds)
                .where('name', '=', AttributeName.bounce_file)
                .execute();

        const projectsWithAttributes = projects.map(project => ({
            ...project,
            attributes: {
                bounce_file: projectAttributes.find(a => a.parent_id === project.id)?.value
            }
        }));

        return {
            files,
            projects: projectsWithAttributes,
        };
    });

    public getBreadcrumbInfoForFile = createZodAction(z.object({
        file_id: z.string(),
    }), async (args) => {
        const db = this.getDB();
        const file = await db.selectFrom('File').selectAll()
            .where('File.id', '=', args.file_id)
            .executeTakeFirst();

        if (!file) {
            return null;
        }

        if (!await checkWorkspaceMembership(this.moduleAPI, file)) {
            throw new Error('Not a member of workspace');
        }

        const projectAttachments = await db.selectFrom('Attachment')
            .where('Attachment.parent_type', '=', AttachmentParentType.Project)
            .where('Attachment.child_type', '=', AttachmentChildType.File)
            .where('Attachment.child_id', '=', args.file_id)
            .selectAll()
            .execute();

        if (!projectAttachments.length) {
            return null;
        }

        const projectId = projectAttachments[0].parent_id;
        const project = await db.selectFrom('Project').selectAll()
            .where('Project.id', '=', projectId)
            .executeTakeFirst();

        if (!project) {
            return null;
        }

        // get parent projects
        const parentProjects = await db.selectFrom('Project')
            .where('Project.id', 'in', project.project_ancestry?.split(',') || [])
            .selectAll()
            .execute();

        const workspace = await db.selectFrom('Workspace').selectAll()
            .where('Workspace.id', '=', file.workspace_id)
            .executeTakeFirst();

        if (!workspace) {
            return null;
        }

        return {
            project,
            file,
            workspace,
            parent_projects: parentProjects,
        };
    });

    public getFilesForWorkspaceWithRaw = createZodAction(z.object({
        workspace_id: z.string(),
    }), async (args) => {
        // TODO: this needs not be available via API
        const db = this.getDB();

        if (!await checkWorkspaceMembership(this.moduleAPI, args)) {
            throw new Error('Not a member of workspace');
        }

        const filesInWorkspace = await db.selectFrom('File')
            .selectAll('File')
            .where('workspace_id', '=', args.workspace_id)
            .execute();

        const fileIds = filesInWorkspace.map(file => file.id);

        const rawFiles = await db.selectFrom('RawFile')
            .where('RawFile.file_id', 'in', fileIds)
            .select('RawFile.full_path')
            .select('RawFile.file_id')
            .orderBy('RawFile.created_at', 'desc')
            .groupBy('RawFile.file_id')
            .execute();

        const filePairs = filesInWorkspace.map(file => {
            const rawFile = rawFiles.find(rawFile => rawFile.file_id === file.id);
            return {
                ...file,
                full_path: rawFile?.full_path,
            };
        });

        return filePairs;
    });

    public getRawFileById = createZodAction(z.object({
        id: z.string().min(1),
    }), async (args) => {
        const db = this.getDB();

        const userContext = await getCurrentUserContext(this.moduleAPI);
        if (!userContext) {
            throw new Error('Not logged in');
        }

        const file = await db.selectFrom('RawFile')
            .where('RawFile.id', '=', args.id)
            .selectAll()
            .executeTakeFirst();

        if (!file) {
            throw new Error('File not found');
        }

        // if (!await checkWorkspaceMembership(this.moduleAPI, file)) {
        //     throw new Error('Not a member of workspace');
        // }

        return file;
    });

    public getFileInfoWithRaw = createZodAction(z.object({
        file_id: z.string()
    }), async (args) => {
        const db = this.getDB();
        const userContext = await getCurrentUserContext(this.moduleAPI);
        if (!userContext) {
            throw new Error('Not logged in');
        }

        const latestRevision = await db.selectFrom('FileRevision')
            .selectAll()
            .where('file_id', '=', args.file_id)
            .orderBy('revision_num', 'desc')
            .executeTakeFirst();

        if (!latestRevision) {
            throw new Error('Latest revision not found');
        }

        const isUserWorkspace = await db.selectFrom('User')
            .where('id', '=', latestRevision.workspace_id)
            .selectAll()
            .executeTakeFirst();

        if (!isUserWorkspace && !await checkWorkspaceMembership(this.moduleAPI, latestRevision)) {
            throw new Error('Not a member of workspace');
        }

        const file = await db.selectFrom('File')
            .innerJoin('RawFile', 'RawFile.file_id', 'File.id')
            .selectAll('File')
            .select('RawFile.full_path')
            .select('RawFile.system')
            .select('RawFile.id as raw_file_id')
            .where('File.id', '=', args.file_id)
            .where('RawFile.file_revision_id', '=', latestRevision.id)
            .executeTakeFirst();
        if (!file) {
            return null;
        }

        const isStarred = await db.selectFrom('Star')
            .select('id')
            .where('parent_id', '=', args.file_id)
            .where('parent_type', '=', 'File')
            .where('created_by', '=', userContext.id)
            .executeTakeFirst();

        return {
            ...file,
            isStarred: Boolean(isStarred),
        };
    });

    public getUser = createZodAction(z.object({
        user_id: z.string(),
    }), async (args) => {
        const db = this.getDB();

        if (!args.user_id) {
            return null;
        }

        const userContext = await getCurrentUserContext(this.moduleAPI);
        if (!userContext) {
            return null;
        }

        // ensure user shares a workspace with the current user
        const myWorkspaces = await db.selectFrom('WorkspaceMember')
            .select('workspace_id')
            .where('user_id', '=', userContext.id)
            .execute();

        const theirWorkspaces = await db.selectFrom('WorkspaceMember')
            .select('workspace_id')
            .where('user_id', '=', args.user_id)
            .execute();

        if (!myWorkspaces.some(workspace => theirWorkspaces.some(workspace2 => workspace.workspace_id === workspace2.workspace_id))) {
            return null;
        }

        return db.selectFrom('User').selectAll()
            .where('id', '=', args.user_id)
            .executeTakeFirst();
    });

    public getUsers = createZodAction(z.object({}), async () => {
        const db = this.getDB();

        const userContext = await getCurrentUserContext(this.moduleAPI);
        if (!userContext) {
            throw new Error('Not logged in');
        }

        const myWorkspaces = await db.selectFrom('WorkspaceMember')
            .select('workspace_id')
            .where('user_id', '=', userContext.id)
            .execute();

        const theirWorkspaces = await db.selectFrom('WorkspaceMember')
            .select('user_id')
            .where('workspace_id', 'in', myWorkspaces.map(workspace => workspace.workspace_id))
            .execute();

        const userIds = Array.from(new Set(theirWorkspaces.map(workspace => workspace.user_id)));

        return db.selectFrom('User').selectAll()
            .where('id', 'in', userIds)
            .execute();
    });

    public getUsersForWorkspace = createZodAction(z.object({
        workspace_id: z.string(),
        excludeSelf: z.boolean(),
    }), async (args) => {
        const db = this.getDB();

        if (!await checkWorkspaceMembership(this.moduleAPI, {workspace_id: args.workspace_id})) {
            throw new Error('Not a member of workspace');
        }

        const userContext = await getCurrentUserContext(this.moduleAPI);
        if (!userContext) {
            throw new Error('Not logged in');
        }

        const theirWorkspaces = await db.selectFrom('WorkspaceMember')
            .select('user_id')
            .where('workspace_id', '=', args.workspace_id)
            .execute();

        let userIds = Array.from(new Set(theirWorkspaces.map(workspace => workspace.user_id)));
        if (args.excludeSelf) {
            userIds = userIds.filter(uid => uid !== userContext.id);
        }

        return db.selectFrom('User').selectAll()
            .where('id', 'in', userIds)
            .execute();
    });

    public getMe = createZodAction(z.object({}), async () => {
        const db = this.getDB();

        const userContext = await getCurrentUserContext(this.moduleAPI);
        if (!userContext) {
            throw new Error('Not logged in');
        }

        return db.selectFrom('User').selectAll()
            .where('id', '=', userContext.id)
            .executeTakeFirst();
    });

    public getRecentProjectsForUser = createZodAction(z.object({
        user_id: z.string(),
        workspace_id: z.string(),
        limit: z.number().optional().default(3),
    }), async (args) => {
        const db = this.getDB();

        if (!await checkWorkspaceMembership(this.moduleAPI, {workspace_id: args.workspace_id})) {
            throw new Error('Not a member of workspace');
        }

        const projects = await db
            .selectFrom('ProjectRevision')
            .innerJoin('Project', 'Project.id', 'ProjectRevision.project_id')
            .where('ProjectRevision.created_by', '=', args.user_id)
            .where('ProjectRevision.workspace_id', '=', args.workspace_id)
            .select([
                'Project.id',
                'Project.name',
            ])
            .select(eb => eb.fn.max('ProjectRevision.created_at').as('latest_activity'))
            .groupBy('Project.id')
            .orderBy('latest_activity', 'desc')
            .limit(args.limit)
            .execute();

        const projectIds = projects.map(p => p.id);
        if (projectIds.length === 0) {
            return [];
        }

        const bounceFileAttributes = await db.selectFrom('Attribute')
            .selectAll()
            .where('parent_type', '=', 'Project')
            .where('parent_id', 'in', projectIds)
            .where('name', '=', AttributeName.bounce_file)
            .execute();

        const bounceFilePaths = bounceFileAttributes.map(a => a.value);
        const bounceFiles = bounceFilePaths.length === 0 ? [] : await db.selectFrom('File')
            .selectAll()
            .where('relative_path', 'in', bounceFilePaths)
            .where('workspace_id', '=', args.workspace_id)
            .execute();

        const projectsWithBounceFiles = projects.map(project => {
            const bounceAttr = bounceFileAttributes.find(a => a.parent_id === project.id);
            const bounceFile = bounceAttr ? bounceFiles.find(f => f.relative_path === bounceAttr.value) : null;
            return {
                ...project,
                bounceFile: bounceFile || null,
            };
        });

        return projectsWithBounceFiles;
    });

    public getCommentsAndRepliesForParent = createZodAction(z.object({
        parent_id: z.string(),
        parent_type: z.enum(['File', 'Comment', 'Project']),
    }), async (args) => {
        const db = this.getDB();

        const {workspace_id} = await db.selectFrom(args.parent_type as 'File')
            .select('workspace_id')
            .where('id', '=', args.parent_id)
            .executeTakeFirstOrThrow();

        if (!await checkWorkspaceMembership(this.moduleAPI, {workspace_id})) {
            throw new Error('Not a member of workspace');
        }

        const topLevelComments = await db
            .selectFrom('Attachment as a')
            .innerJoin('Comment as c', 'a.child_id', 'c.id')
            .selectAll('c')
            .where('a.parent_id', '=', args.parent_id)
            .where('a.parent_type', '=', args.parent_type)
            .where('a.workspace_id', '=', workspace_id)
            .where('c.workspace_id', '=', workspace_id)
            .orderBy('c.created_at', 'asc')
            .execute();

        const commentIds = topLevelComments.map(comment => comment.id);
        if (commentIds.length === 0) {
            return [];
        }

        const commentMap = new Map<string, NestedComment>();

        topLevelComments.forEach(row => {
            if (!row.reply_parent_id) {
                commentMap.set(row.id, {
                    ...row,
                    replies: [],
                });
            }
        });

        topLevelComments.forEach(row => {
            if (row.reply_parent_id) {
                const parentComment = commentMap.get(row.reply_parent_id);
                if (parentComment) {
                    parentComment.replies.push({
                        ...row,
                        replies: [],
                    });
                }
            }
        });

        return Array.from(commentMap.values());
    });

    public getAllWorkspaces = createZodAction(z.object({}), async () => {
        const db = this.getDB();
        const userContext = await getCurrentUserContext(this.moduleAPI);

        const workspaces = await db.selectFrom('Workspace')
            .innerJoin('WorkspaceMember', 'WorkspaceMember.workspace_id', 'Workspace.id')
            .selectAll('Workspace')
            .where('WorkspaceMember.user_id', '=', userContext.id)
            .where('Workspace.type', '!=', 'USER')
            .execute();

        return workspaces;
    });

    public getAllWorkspacesWithStats = createZodAction(z.object({}), async () => {
        const db = this.getDB();
        const userContext = await getCurrentUserContext(this.moduleAPI);

        const workspaces = await db.selectFrom('Workspace')
            .innerJoin('WorkspaceMember', 'WorkspaceMember.workspace_id', 'Workspace.id')
            .selectAll('Workspace')
            .where('WorkspaceMember.user_id', '=', userContext.id)
            .where('Workspace.type', '!=', 'USER')
            .execute();

        const workspacesWithStats = await Promise.all(workspaces.map(async (workspace) => {
            const memberCount = await db.selectFrom('WorkspaceMember')
                .select(db.fn.count<number>('id').as('count'))
                .where('workspace_id', '=', workspace.id)
                .executeTakeFirst();

            const projectCount = await db.selectFrom('Project')
                .select(db.fn.count<number>('id').as('count'))
                .where('workspace_id', '=', workspace.id)
                .where('parent_id', 'is', null)
                // .where('hidden_at', 'is', null)
                .executeTakeFirst();

            return {
                ...workspace,
                memberCount: Number(memberCount?.count || 0),
                projectCount: Number(projectCount?.count || 0),
            };
        }));

        return workspacesWithStats;
    });

    public getWorkspace = createZodAction(z.object({
        workspace_id: z.string(),
    }), async (args) => {
        const db = this.getDB();
        const userContext = await getCurrentUserContext(this.moduleAPI);

        const workspace = await db.selectFrom('Workspace')
            .innerJoin('WorkspaceMember', 'WorkspaceMember.workspace_id', 'Workspace.id')
            .selectAll('Workspace')
            .where('WorkspaceMember.user_id', '=', userContext.id)
            .where('Workspace.id', '=', args.workspace_id)
            .executeTakeFirst();

        return workspace;
    });

    public getWorkspaceMembers = createZodAction(z.object({
        workspace_id: z.string(),
    }), async (args) => {
        const db = this.getDB();

        const members = await db.selectFrom('WorkspaceMember')
            .innerJoin('User', 'User.id', 'WorkspaceMember.user_id')
            .innerJoin('Workspace', 'Workspace.id', 'WorkspaceMember.workspace_id')
            .select([
                'WorkspaceMember.id',
                'WorkspaceMember.user_id',
                'WorkspaceMember.role',
                'WorkspaceMember.created_at',
                'User.name',
                'Workspace.owner_id',
            ])
            .where('WorkspaceMember.workspace_id', '=', args.workspace_id)
            .orderBy('WorkspaceMember.created_at', 'asc')
            .execute();

        return members;
    });

    public getProjectsForWorkspace = createZodAction(z.object({
        workspace_id: z.string(),
        topLevelOnly: z.boolean().optional().default(false),
        includeHidden: z.boolean().optional().default(false),
    }), async (args) => {
        const db = this.getDB();

        if (!args.workspace_id) {
            throw new Error('No workspace id provided');
        }

        if (!(await checkWorkspaceMembership(this.moduleAPI, {workspace_id: args.workspace_id}))) {
            throw new Error('User is not a member of this workspace');
        }

        const projects = await db.selectFrom('Project')
            .innerJoin('Workspace', 'Workspace.id', 'Project.workspace_id')
            .leftJoin('Attachment', (join) => join
                .onRef('Attachment.child_id', '=', 'Project.id')
                .on('Attachment.child_type', '=', 'Project')
                .on('Attachment.parent_type', '=', 'Workspace')
                .onRef('Attachment.parent_id', '=', 'Project.workspace_id')
            )
            .selectAll('Project')
            .select(['Attachment.index as attachment_index', 'Attachment.hidden_at as attachment_hidden_at'])
            .where('Project.workspace_id', '=', args.workspace_id)
            .$if(!args.includeHidden, (qb) => qb.where('Attachment.hidden_at', 'is', null))
            .$if(args.topLevelOnly, (qb) => qb.where('Project.parent_id', 'is', null))
            .orderBy('Attachment.index', 'asc')
            .orderBy('Project.created_at', 'asc')
            .execute();

        const files = await getFilesForProjects(db, projects.map(p => p.id));
        const projectsWithFiles = projects.map(project => ({
            ...project,
            hidden_at: project.attachment_hidden_at,
            files: files.filter(f => f.project_id === project.id),
        }))

        return projectsWithFiles;
    });

    public getProject = createZodAction(z.object({
        project_id: z.string(),
        includeHidden: z.boolean().optional().default(false),
    }), async (args) => {
        const db = this.getDB();

        const project = await db.selectFrom('Project')
            .selectAll()
            .where('Project.id', '=', args.project_id)
            .executeTakeFirst();

        if (!project) {
            throw new Error(`No project found with id ${args.project_id}`);
        }

        if (!(await checkWorkspaceMembership(this.moduleAPI, {workspace_id: project.workspace_id}))) {
            throw new Error('User is not a member of this workspace');
        }

        const attributeRows = await db.selectFrom('Attribute')
            .selectAll()
            .where('Attribute.parent_type', '=', 'Project')
            .where('Attribute.parent_id', '=', args.project_id)
            .execute();

        const attributes = {
            [AttributeName.description]: attributeRows.find(a => a.name === AttributeName.description)?.value,
            [AttributeName.chord_lyrics]: attributeRows.find(a => a.name === AttributeName.chord_lyrics)?.value,
            [AttributeName.launcher_file]: attributeRows.find(a => a.name === AttributeName.launcher_file)?.value,
            [AttributeName.bounce_file]: attributeRows.find(a => a.name === AttributeName.bounce_file)?.value,
        };

        const subProjects = await db.selectFrom('Project')
            .leftJoin('Attachment', (join) => join
                .onRef('Attachment.child_id', '=', 'Project.id')
                .on('Attachment.child_type', '=', 'Project')
                .on('Attachment.parent_type', '=', 'Project')
                .onRef('Attachment.parent_id', '=', 'Project.parent_id')
            )
            .selectAll('Project')
            .select(['Attachment.index as attachment_index', 'Attachment.hidden_at as attachment_hidden_at'])
            .where('Project.parent_id', '=', args.project_id)
            .$if(!args.includeHidden, (qb) => qb.where('Attachment.hidden_at', 'is', null))
            .orderBy('Attachment.index', 'asc')
            .orderBy('Project.created_at', 'asc')
            .execute();

        const files = await getFilesForProjects(db, subProjects.map(p => p.id));

        const subProjectsWithFiles = subProjects.map(project => ({
            ...project,
            hidden_at: project.attachment_hidden_at,
            files: files.filter(f => f.project_id === project.id),
        }))

        // Get the number of project revisions
        const revisionCount = await db.selectFrom('ProjectRevision')
            .where('project_id', '=', args.project_id)
            .select(eb => eb.fn.max<number>('ProjectRevision.revision_num').as('num_revisions'))
            .executeTakeFirst();

        const userContext = await getCurrentUserContext(this.moduleAPI);
        const isStarred = userContext ? await db.selectFrom('Star')
            .select('id')
            .where('parent_id', '=', args.project_id)
            .where('parent_type', '=', 'Project')
            .where('created_by', '=', userContext.id)
            .executeTakeFirst() : null;

        return {
            ...project,
            attributes,
            sub_projects: subProjectsWithFiles,
            num_revisions: revisionCount?.num_revisions || 0,
            isStarred: Boolean(isStarred),
        };
    });

    public getProjectWithAncestors = createZodAction(z.object({
        project_id: z.string(),
    }), async (args) => {
        const db = this.getDB();

        const project = await db.selectFrom('Project')
            .selectAll()
            .where('Project.id', '=', args.project_id)
            .executeTakeFirst();

        if (!project) {
            throw new Error(`No project found with id ${args.project_id}`);
        }

        if (!(await checkWorkspaceMembership(this.moduleAPI, {workspace_id: project.workspace_id}))) {
            throw new Error('User is not a member of this workspace');
        }

        const parentProjectIds = project.project_ancestry?.split(',') || [];

        const parentProjects = parentProjectIds.length > 0 ? await db.selectFrom('Project')
            .selectAll()
            .where('Project.id', 'in', parentProjectIds)
            .execute() : [];

        const workspace = await db.selectFrom('Workspace')
            .selectAll()
            .where('Workspace.id', '=', project.workspace_id)
            .executeTakeFirst();

        if (!workspace) {
            return null;
        }

        return {
            ...project,
            parent_projects: parentProjects,
            workspace,
        };
    });

    public searchEverything = createZodAction(z.object({
        query: z.string(),
    }), async (args) => {
        const db = this.getDB();

        // Prepare the query for FTS5 search
        // 1. Escape any special characters that might interfere with FTS syntax
        // 2. Add * for prefix matching on the last term
        const searchQuery = args.query
            .trim()
            .replace(/[^a-zA-Z0-9 _-]/g, '')
            .split(/\s+/)                // Split by whitespace
            .filter(Boolean)             // Remove empty strings
            .map((term, i, arr) => {
                // Add wildcard to the last term for prefix matching
                if (i === arr.length - 1) {
                    return `${term}*`;
                }
                return term;
            })
            .join(' ');      // Join with spaces for AND logic

        // if (!searchQuery) {
        //     return [];
        // }

        const myWorkspaces = await this.getAllWorkspaces({}, {workspace_id: null});

        const results = await db.selectFrom('Project_fts' as 'Project')
            .select('id')
            .select('name')
            .select('workspace_id')
            .$if(searchQuery.length > 0, (qb) => qb.where('Project_fts' as 'name', sql`match`, searchQuery))
            .where('workspace_id', 'in', myWorkspaces.map(w => w.id))
            .execute();

        const ids = Array.from(new Set(results.map(r => r.id)));

        return db.selectFrom('Project')
            .selectAll()
            .where('id', 'in', ids)
            .execute();
    });

    public getFilesForProject = createZodAction(z.object({
        project_id: z.string(),
        project_version_id: z.string().optional(),
    }), async (args) => {
        const db = this.getDB();

        const project = await db.selectFrom('Project')
            .select('workspace_id')
            .where('id', '=', args.project_id)
            .executeTakeFirstOrThrow();

        if (!(await checkWorkspaceMembership(this.moduleAPI, {workspace_id: project.workspace_id}))) {
            throw new Error('User is not a member of this workspace');
        }

        const attachments = await this.getAttachmentIdsForParentAndChild({
            parent_type: 'Project',
            parent_id: args.project_id,
            child_type: 'File',
        }, {workspace_id: project.workspace_id});

        return db.selectFrom('File')
            .innerJoin('FileRevision', 'File.id', 'FileRevision.file_id')
            .innerJoin('RawFile', 'FileRevision.id', 'RawFile.file_revision_id')
            .leftJoin('Attribute as DurationAttr', (join) =>
                join
                    .onRef('DurationAttr.parent_id', '=', 'File.id')
                    .on('DurationAttr.parent_type', '=', 'File')
                    .on('DurationAttr.name', '=', 'duration')
            )
            .selectAll('File')
            .select('RawFile.hash')
            .select('RawFile.last_modified_at')
            .select('RawFile.created_at')
            .select('RawFile.size')
            .select('RawFile.system')
            .select('RawFile.file_revision_id')
            .select('DurationAttr.value as duration')
            .select(eb => eb.fn.max<number>('FileRevision.revision_num').as('num_revisions'))
            .where('File.id', 'in', attachments.map(a => a.child_id))
            .groupBy(['File.id', 'DurationAttr.value'])
            .execute();
    });

    public fetchAllAttachmentsForWorkspace = createZodAction(z.object({}), async () => {

    });

    public getAttachmentIdsForParentAndChild = createZodAction(z.object({
        parent_type: z.enum(['File', 'Project']),
        parent_id: z.string(),
        child_type: z.enum(['File', 'Comment']),
    }), async (args) => {
        const db = this.getDB();

        const parentObject = await db.selectFrom(args.parent_type as 'File')
            .select('workspace_id')
            .where('id', '=', args.parent_id)
            .executeTakeFirst();

        if (!parentObject) {
            throw new Error(`No ${args.parent_type} found with id ${args.parent_id}`);
        }

        if (!(await checkWorkspaceMembership(this.moduleAPI, {workspace_id: parentObject.workspace_id}))) {
            throw new Error('User is not a member of this workspace');
        }

        return db.selectFrom('Attachment')
            .select('Attachment.child_id')
            .where('Attachment.workspace_id', '=', parentObject.workspace_id)
            .where('Attachment.parent_type', '=', args.parent_type)
            .where('Attachment.parent_id', '=', args.parent_id)
            .where('Attachment.child_type', '=', args.child_type)
            .execute();
    });

    public getRecentActivityForAllWorkspaces = createZodAction(z.object({
    }), async (args) => {
        const db = this.getDB();

        const userContext = await getCurrentUserContext(this.moduleAPI);

        const myUserActivityPromise = db.selectFrom('UserActivity as ua')
            .where('ua.created_by', '=', userContext.id)
            .innerJoin(
                eb => eb.selectFrom('UserActivity as ua2')
                    .select(['object_id', 'object_type', 'created_by'])
                    .select(eb => eb.fn.max('created_at').as('max_created_at'))
                    .where('created_by', '=', userContext.id)
                    .groupBy(['object_id', 'object_type'])
                    .orderBy('ua2.created_at', 'desc')
                    .limit(10)
                    .as('latest'),
                join => join
                    .onRef('ua.object_id', '=', 'latest.object_id')
                    .onRef('ua.object_type', '=', 'latest.object_type')
                    .onRef('ua.created_by', '=', 'latest.created_by')
                    .onRef('ua.created_at', '=', 'latest.max_created_at')
            )
            .selectAll('ua')
            .execute();

        const myUserActivity = await myUserActivityPromise;

        const workspaceIds = (await this.getAllWorkspaces({}, {workspace_id: null})).map(w => w.id);
        const recentAttachments = await db.selectFrom('Attachment')
            .selectAll()
            .where('Attachment.workspace_id', 'in', workspaceIds)
            .limit(10)
            .orderBy('Attachment.created_at', 'desc')
            .execute();

        const recentActivityAttachmentIds = recentAttachments.map(a => a.id);
        const myUserActivityObjectIds = myUserActivity.map(a => a.object_id);

        const myActivityAttachments = await db.selectFrom('Attachment')
            .selectAll()
            .where('Attachment.id', 'not in', recentActivityAttachmentIds)
            .where(eb => eb.or([
                eb('Attachment.parent_id', 'in', myUserActivityObjectIds),
                eb('Attachment.child_id', 'in', myUserActivityObjectIds),
            ]))
            .orderBy('Attachment.created_at', 'desc')
            .execute();

        const allAttachments = [...recentAttachments, ...myActivityAttachments].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

        const filesToFetch: Set<string> = new Set();
        const projectsToFetch: Set<string> = new Set();
        const commentsToFetch: Set<string> = new Set();
        const usersToFetch: Set<string> = new Set();
        for (const attachment of allAttachments) {
            const parentType = attachment.parent_type;
            const childType = attachment.child_type;

            usersToFetch.add(attachment.created_by);

            if (parentType === 'File') {
                filesToFetch.add(attachment.parent_id);
            }
            if (parentType === 'Project') {
                projectsToFetch.add(attachment.parent_id);
            }

            if (childType === 'Comment') {
                commentsToFetch.add(attachment.child_id);
            }
            if (childType === 'File') {
                filesToFetch.add(attachment.child_id);
            }
        }

        for (const activity of myUserActivity) {
            const objectType = activity.object_type;

            if (objectType === 'File') {
                filesToFetch.add(activity.object_id);
            }
            if (objectType === 'Project') {
                projectsToFetch.add(activity.object_id);
            }
            if (objectType === 'Comment') {
                commentsToFetch.add(activity.object_id);
            }
        }

        const filesPromise = !filesToFetch.size ? Promise.resolve([]) :
            db.selectFrom('File').where('id', 'in', Array.from(filesToFetch)).orderBy('created_at', 'desc').selectAll().execute();

        const commentsPromise = !commentsToFetch.size ? Promise.resolve([]) :
            db.selectFrom('Comment').where('id', 'in', Array.from(commentsToFetch)).orderBy('created_at', 'desc').selectAll().execute();

        const projectsPromise = !projectsToFetch.size ? Promise.resolve([]) :
            db.selectFrom('Project').where('id', 'in', Array.from(projectsToFetch)).orderBy('created_at', 'desc').selectAll().execute();

        const usersPromise = !usersToFetch.size ? Promise.resolve([]) :
            db.selectFrom('User').where('id', 'in', Array.from(usersToFetch)).orderBy('created_at', 'desc').selectAll().execute();

        const [
            files,
            comments,
            projects,
            users,
        ] = await Promise.all([
            filesPromise,
            commentsPromise,
            projectsPromise,
            usersPromise,
        ]);

        return {
            // workspaces,
            files,
            comments,
            projects,
            attachments: allAttachments,
            users,
            myUserActivity,
        };
    });

    public getDashboardData = createZodAction(z.object({
        workspace_id: z.string().optional(),
    }), async (args) => {
        const db = this.getDB();
        const userContext = await getCurrentUserContext(this.moduleAPI);

        // Get user activity
        const myUserActivityPromise = db.selectFrom('UserActivity as ua')
            .where('ua.created_by', '=', userContext.id)
            .innerJoin(
                eb => eb.selectFrom('UserActivity as ua2')
                    .select(['object_id', 'object_type', 'created_by'])
                    .select(eb => eb.fn.max('created_at').as('max_created_at'))
                    .where('created_by', '=', userContext.id)
                    .groupBy(['object_id', 'object_type'])
                    .orderBy('ua2.created_at', 'desc')
                    .limit(10)
                    .as('latest'),
                join => join
                    .onRef('ua.object_id', '=', 'latest.object_id')
                    .onRef('ua.object_type', '=', 'latest.object_type')
                    .onRef('ua.created_by', '=', 'latest.created_by')
                    .onRef('ua.created_at', '=', 'latest.max_created_at')
            )
            .selectAll('ua')
            .execute();

        const myUserActivity = await myUserActivityPromise;

        const allWorkspaces = await this.getAllWorkspaces({}, {workspace_id: null});
        const workspaceIds = args.workspace_id
            ? [args.workspace_id]
            : allWorkspaces.map(w => w.id);

        const projectsToFetch: Set<string> = new Set();
        const commentsToFetch: Set<string> = new Set();
        const usersToFetch: Set<string> = new Set();

        for (const activity of myUserActivity) {
            usersToFetch.add(activity.created_by);

            if (activity.object_type === 'Project') projectsToFetch.add(activity.object_id);
            if (activity.object_type === 'Comment') commentsToFetch.add(activity.object_id);
        }

        const recentProjects = await db.selectFrom('Project')
            .selectAll()
            .where('workspace_id', 'in', workspaceIds)
            .orderBy('created_at', 'desc')
            .limit(50)
            .execute();

        recentProjects.forEach(project => {
            projectsToFetch.add(project.id);
            usersToFetch.add(project.created_by);
        });

        // Fetch all files attached to the projects we're displaying
        const filesPromise = !projectsToFetch.size ? Promise.resolve([]) :
            db.selectFrom('File')
                .innerJoin('Attachment', join => join
                    .onRef('Attachment.child_id', '=', 'File.id')
                    .on('Attachment.child_type', '=', 'File')
                    .on('Attachment.parent_type', '=', 'Project'))
                .where('Attachment.parent_id', 'in', Array.from(projectsToFetch))
                .selectAll('File')
                .distinct()
                .execute();

        const commentsPromise = !commentsToFetch.size ? Promise.resolve([]) :
            db.selectFrom('Comment').where('id', 'in', Array.from(commentsToFetch))
                .orderBy('created_at', 'desc').selectAll().execute();

        const projectsPromise = !projectsToFetch.size ? Promise.resolve([]) :
            db.selectFrom('Project')
                .where('id', 'in', Array.from(projectsToFetch))
                .where('workspace_id', 'in', workspaceIds)
                .orderBy('created_at', 'desc')
                .selectAll()
                .execute();

        const usersPromise = !usersToFetch.size ? Promise.resolve([]) :
            db.selectFrom('User').where('id', 'in', Array.from(usersToFetch))
                .orderBy('created_at', 'desc').selectAll().execute();

        const projectRevisionsPromise = db.selectFrom('ProjectRevision')
            .innerJoin('Project', 'Project.id', 'ProjectRevision.project_id')
            .where('Project.workspace_id', 'in', workspaceIds)
            .orderBy('ProjectRevision.created_at', 'desc')
            .limit(100)
            .selectAll('ProjectRevision')
            .execute();


                // promises.push(this.attachments.create({
                //     workspace_id: project.workspace_id,
                //     parent_type: AttachmentParentType.ProjectRevision,
                //     parent_id: newProjectRevisionId,
                //     child_type: AttachmentChildType.FileRevision,
                //     child_id: revision.id,
                // }));
        // Fetch FileRevisions for counting files in each project revision
        // First get all project revision IDs for the projects we're interested in
        const projectRevisionIdsPromise = !projectsToFetch.size ? Promise.resolve([]) :
            db.selectFrom('ProjectRevision')
                .where('project_id', 'in', Array.from(projectsToFetch))
                .select(['id'])
                .execute();

        // Then fetch file revisions using those project revision IDs
        const fileRevisionsPromise = projectRevisionIdsPromise.then(async projectRevisions => {
            if (projectRevisions.length === 0) return [];

            const projectRevisionIds = projectRevisions.map(pr => pr.id);

            // Get all attachments between project revisions and file revisions
            const attachments = await db.selectFrom('Attachment')
                .where('Attachment.parent_id', 'in', projectRevisionIds)
                .where('Attachment.child_type', '=', 'FileRevision')
                .where('Attachment.parent_type', '=', 'ProjectRevision')
                .select([
                    'Attachment.child_id as file_revision_id',
                    'Attachment.parent_id as project_revision_id'
                ])
                .execute();

            // Get all file revisions referenced in the attachments
            if (attachments.length === 0) return [];

            const fileRevisionIds = Array.from(new Set(attachments.map(a => a.file_revision_id)));
            const fileRevisions = await db.selectFrom('FileRevision')
                .where('id', 'in', fileRevisionIds)
                .selectAll()
                .execute();

            // Combine file revisions with their project revision IDs
            const result = attachments.map(attachment => {
                const fileRevision = fileRevisions.find(fr => fr.id === attachment.file_revision_id);
                if (!fileRevision) return null;

                return {
                    ...fileRevision,
                    project_revision_id: attachment.project_revision_id
                };
            }).filter(Boolean); // Remove nulls

            return result;
        });

        const [files, comments, projects, users, projectRevisions, fileRevisions] = await Promise.all([
            filesPromise, commentsPromise, projectsPromise, usersPromise, projectRevisionsPromise, fileRevisionsPromise
        ]);

        const allProjectIds = new Set(projectsToFetch);
        const allUserIds = new Set(usersToFetch);
        projectRevisions.forEach(pr => {
            allProjectIds.add(pr.project_id);
            allUserIds.add(pr.created_by);
        });

        const missingProjectIds = Array.from(allProjectIds).filter(id => !projects.find(p => p.id === id));
        const missingUserIds = Array.from(allUserIds).filter(id => !users.find(u => u.id === id));

        const [missingProjects, missingUsers, missingProjectFiles] = await Promise.all([
            missingProjectIds.length === 0 ? Promise.resolve([]) :
                db.selectFrom('Project').where('id', 'in', missingProjectIds).selectAll().execute(),
            missingUserIds.length === 0 ? Promise.resolve([]) :
                db.selectFrom('User').where('id', 'in', missingUserIds).selectAll().execute(),
            missingProjectIds.length === 0 ? Promise.resolve([]) :
                db.selectFrom('File')
                    .innerJoin('Attachment', join => join
                        .onRef('Attachment.child_id', '=', 'File.id')
                        .on('Attachment.child_type', '=', 'File')
                        .on('Attachment.parent_type', '=', 'Project'))
                    .where('Attachment.parent_id', 'in', missingProjectIds)
                    .selectAll('File')
                    .distinct()
                    .execute(),
        ]);

        const allProjects = [...projects, ...missingProjects];
        const allUsers = [...users, ...missingUsers];
        const allFiles = [...files, ...missingProjectFiles];

        const allProjectFileAttachments = await db.selectFrom('Attachment')
            .selectAll()
            .where('Attachment.parent_id', 'in', allProjects.map(p => p.id))
            .where('Attachment.parent_type', '=', 'Project')
            .where('Attachment.child_type', '=', 'File')
            .execute();

        const commentAttachmentsPromise = commentsToFetch.size === 0 ? Promise.resolve([]) :
            db.selectFrom('Attachment')
                .selectAll()
                .where('Attachment.child_id', 'in', Array.from(commentsToFetch))
                .where('Attachment.child_type', '=', 'Comment')
                .execute();

        const commentAttachments = await commentAttachmentsPromise;

        const mergedAttachments = [...allProjectFileAttachments, ...commentAttachments];

        // Fetch only necessary attributes (bounce_file for projects, duration for files)
        const projectIds = Array.from(allProjectIds);
        const fileIds = allFiles.map(f => f.id);

        const projectAttributesPromise = projectIds.length === 0 ? Promise.resolve([]) :
            db.selectFrom('Attribute')
                .select(['parent_id', 'name', 'value'])
                .where('parent_type', '=', 'Project')
                .where('parent_id', 'in', projectIds)
                .where('name', '=', AttributeName.bounce_file)
                .execute();

        const fileAttributesPromise = fileIds.length === 0 ? Promise.resolve([]) :
            db.selectFrom('Attribute')
                .select(['parent_id', 'name', 'value'])
                .where('parent_type', '=', 'File')
                .where('parent_id', 'in', fileIds)
                .where('name', '=', 'duration')
                .execute();

        const [projectAttributes, fileAttributes] = await Promise.all([
            projectAttributesPromise, fileAttributesPromise
        ]);

        // Map attributes to their parent objects
        const projectsWithAttributes = allProjects.map(project => ({
            ...project,
            attributes: {
                bounce_file: projectAttributes.find(a => a.parent_id === project.id)?.value
            }
        }));

        const filesWithAttributes = allFiles.map(file => ({
            ...file,
            attributes: {
                duration: fileAttributes.find(a => a.parent_id === file.id)?.value
            }
        }));

        // Group project revisions by project_id and sort by revision_num
        const projectRevisionsGroupedByProject = projectRevisions.reduce((acc, pr) => {
            if (!acc[pr.project_id]) {
                acc[pr.project_id] = [];
            }
            acc[pr.project_id].push(pr);
            return acc;
        }, {} as Record<string, typeof projectRevisions>);

        // Sort each group by revision_num
        Object.keys(projectRevisionsGroupedByProject).forEach(projectId => {
            projectRevisionsGroupedByProject[projectId].sort((a, b) => b.revision_num - a.revision_num);
        });

        // Add file_revisions and calculate num_new_files
        const projectRevisionsWithFileRevisions = projectRevisions.map(pr => {
            // Get file revisions for this project revision
            const prFileRevisions = fileRevisions.filter(fr => fr?.project_revision_id === pr.id);

            // Find the previous project revision for the same project
            const projectRevisionGroup = projectRevisionsGroupedByProject[pr.project_id] || [];
            const currentIndex = projectRevisionGroup.findIndex(revision => revision.id === pr.id);
            const previousRevision = currentIndex < projectRevisionGroup.length - 1 ? projectRevisionGroup[currentIndex + 1] : null;

            // Calculate num_new_files
            let numNewFiles = prFileRevisions.length;
            if (previousRevision) {
                const previousFileRevisions = fileRevisions.filter(fr => fr?.project_revision_id === previousRevision.id);
                // Get file IDs from both current and previous revisions
                const currentFileIds = new Set(prFileRevisions.filter(fr => fr !== null).map(fr => fr!.file_id));
                const previousFileIds = new Set(previousFileRevisions.filter(fr => fr !== null).map(fr => fr!.file_id));

                // Count files that are in current but not in previous
                numNewFiles = 0;
                currentFileIds.forEach(fileId => {
                    if (!previousFileIds.has(fileId)) {
                        numNewFiles++;
                    }
                });
            }

            return {
                ...pr,
                file_revisions: prFileRevisions,
                num_new_files: numNewFiles
            };
        });

        return {
            files: filesWithAttributes,
            comments,
            projects: projectsWithAttributes,
            attachments: mergedAttachments,
            users: allUsers,
            myUserActivity,
            projectRevisions: projectRevisionsWithFileRevisions,
        };
    });

    public getFileWaveformAndDuration = createZodAction(z.object({
        file_id: z.string().optional(),
    }), async (args) => {
        const db = this.getDB();

        if (!args.file_id) {
            return null;
        }

        const file = await db.selectFrom('File')
            .selectAll()
            .where('File.id', '=', args.file_id)
            .executeTakeFirstOrThrow();

        if (!(await checkWorkspaceMembership(this.moduleAPI, {workspace_id: file.workspace_id}))) {
            throw new Error('User is not a member of this workspace');
        }

        const attributes = await db.selectFrom('Attribute')
            .selectAll()
            .where('Attribute.parent_type', '=', 'File')
            .where('Attribute.parent_id', '=', args.file_id)
            .where('Attribute.name', 'in', ['waveform_data', 'duration'])
            .execute();

        if (!attributes) {
            return null;
        }

        const duration = attributes.find(a => a.name === 'duration')?.value;
        const waveform_data = attributes.find(a => a.name === 'waveform_data')?.value;

        return {
            duration: duration ? Number(duration) : undefined,
            waveform_data: waveform_data ? (JSON.parse(waveform_data) as number[][]) : undefined,
        };
    });

    public getAttachmentsForThing = createZodAction(z.object({
        parent_type: z.enum(['File', 'Project']),
        parent_id: z.string(),
    }), async (args) => {
        const db = this.getDB();

        const parentObject = await db.selectFrom(args.parent_type as 'File')
            .select('workspace_id')
            .where('id', '=', args.parent_id)
            .executeTakeFirst();

        if (!parentObject) {
            throw new Error(`No ${args.parent_type} found with id ${args.parent_id}`);
        }

        if (!(await checkWorkspaceMembership(this.moduleAPI, {workspace_id: parentObject.workspace_id}))) {
            throw new Error('User is not a member of this workspace');
        }

        return db.selectFrom('Attachment')
            .selectAll()
            .where('Attachment.workspace_id', '=', parentObject.workspace_id)
            .where('Attachment.parent_type', '=', args.parent_type)
            .where('Attachment.parent_id', '=', args.parent_id)
            .execute();
    });

    public getProjectsForFile = createZodAction(z.object({
        file_id: z.string(),
    }), async (args) => {
        const db = this.getDB();

        const userContext = await getCurrentUserContext(this.moduleAPI);
        if (!userContext) {
            throw new Error('User not logged in');
        }

        const file = await db.selectFrom('File')
            .select('workspace_id')
            .where('id', '=', args.file_id)
            .executeTakeFirst();

        if (!file) {
            throw new Error(`No file found with id ${args.file_id}`);
        }

        if (!(await checkWorkspaceMembership(this.moduleAPI, {workspace_id: file.workspace_id}))) {
            throw new Error('User is not a member of this workspace');
        }

        const attachments = await this.getAttachmentsForChildThing({
            child_id: args.file_id,
            child_type: 'File',
            parent_type: 'Project',
        }, {workspace_id: file.workspace_id});

        const projects = await db.selectFrom('Project')
            .selectAll()
            .where('Project.id', 'in', attachments.map(a => a.parent_id))
            .execute();

        return projects;
    });

    public getAttachmentsForChildThing = createZodAction(z.object({
        child_type: z.enum(['File']),
        child_id: z.string(),
        parent_type: z.enum(['File', 'Project']).optional(),
    }), async (args) => {
        const db = this.getDB();

        const childObject = await db.selectFrom(args.child_type as 'File')
            .select('workspace_id')
            .where('id', '=', args.child_id)
            .executeTakeFirst();

        if (!childObject) {
            throw new Error(`No ${args.child_type} found with id ${args.child_id}`);
        }

        if (!(await checkWorkspaceMembership(this.moduleAPI, {workspace_id: childObject.workspace_id}))) {
            throw new Error('User is not a member of this workspace');
        }

        return db.selectFrom('Attachment')
            .selectAll()
            .where('Attachment.workspace_id', '=', childObject.workspace_id)
            .where('Attachment.child_type', '=', args.child_type)
            .where('Attachment.child_id', '=', args.child_id)
            .$if(Boolean(args.parent_type), (qb) => qb.where('Attachment.parent_type', '=', args.parent_type!))
            .execute();
    });

    public getUserPreference = createZodAction(z.object({
        key: z.enum([UserPreferenceType.theme]),
        workspace_id: z.string().min(1).optional(),
    }), async (args) => {
        const db = this.getDB();
        const userContext = await getCurrentUserContext(this.moduleAPI);
        if (!userContext) {
            throw new Error('Not logged in');
        }

        if (args.workspace_id && !await checkWorkspaceMembership(this.moduleAPI, {workspace_id: args.workspace_id})) {
            throw new Error('Not a member of workspace');
        }

        const shouldUseWorkspaceId = Boolean(args.workspace_id);
        const preference = await db.selectFrom('UserPreference')
            .where('user_id', '=', userContext.id)
            .where('key', '=', args.key)
            .$if(shouldUseWorkspaceId, (qb) => qb.where('workspace_id', '=', args.workspace_id!))
            .$if(!shouldUseWorkspaceId, (qb) => qb.where('workspace_id', 'is', null))
            .selectAll()
            .executeTakeFirst();

        return preference || null;
    });

    public getAllUserPreferences = createZodAction(z.object({
        workspace_id: z.string().min(1).optional(),
    }), async (args) => {
        const db = this.getDB();
        const userContext = await getCurrentUserContext(this.moduleAPI);
        if (!userContext) {
            throw new Error('Not logged in');
        }

        if (args.workspace_id && !await checkWorkspaceMembership(this.moduleAPI, {workspace_id: args.workspace_id})) {
            throw new Error('Not a member of workspace');
        }

        const shouldUseWorkspaceId = Boolean(args.workspace_id);
        const preferences = await db.selectFrom('UserPreference')
            .where('user_id', '=', userContext.id)
            .$if(shouldUseWorkspaceId, (qb) => qb.where('workspace_id', '=', args.workspace_id!))
            .$if(!shouldUseWorkspaceId, (qb) => qb.where('workspace_id', 'is', null))
            .selectAll()
            .execute();

        return preferences;
    });

    public getCollaborationRequests = createZodAction(z.object({}), async () => {
        const db = this.getDB();
        const userContext = await getCurrentUserContext(this.moduleAPI);
        if (!userContext) {
            throw new Error('Not logged in');
        }

        return [{
            id: '1',
            projectName: 'Project 1',
            from: 'User 1',
            message: 'Message 1'
        }];
    });

    public getUnreadNotifications = createZodAction(z.object({}), async () => {
        const db = this.getDB();
        const userContext = await getCurrentUserContext(this.moduleAPI);
        if (!userContext) {
            throw new Error('Not logged in');
        }

        return [{
            id: '1',
            from: 'User 1',
            updateType: 'new_files',
            projectName: 'Project 1',
            preview: 'Preview 1',
        }];
    });

    public getUserProfileData = createZodAction(z.object({
        user_id: z.string(),
    }), async (args) => {
        const db = this.getDB();

        const user = await db.selectFrom('User')
            .select(['id', 'name', 'created_at', 'updated_at', 'profile_picture_file_id'])
            .where('id', '=', args.user_id)
            .executeTakeFirst();

        if (!user) {
            return null;
        }

        const publicTracks = await db.selectFrom('ProfilePublicTrack')
            .innerJoin('FileRevision', 'FileRevision.id', 'ProfilePublicTrack.file_revision_id')
            .innerJoin('File', 'File.id', 'FileRevision.file_id')
            .leftJoin('User as FileCreator', 'FileCreator.id', 'File.created_by')
            .select([
                'File.id as file_id',
                'File.name',
                'File.relative_path',
                'File.mime_type',
                'File.size',
                'File.earliest_known_modified_at',
                'File.workspace_id',
                'File.created_by',
                'File.created_at',
                'File.updated_at',
                'File.deleted_at',
                'File.versioning_mode',
                'File.auto_version',
                'FileRevision.id as file_revision_id',
                'FileRevision.revision_num',
                'ProfilePublicTrack.created_at as published_at',
                'FileCreator.name as creator_name',
            ])
            .where('ProfilePublicTrack.user_id', '=', args.user_id)
            .where('File.deleted_at', 'is', null)
            .orderBy('ProfilePublicTrack.created_at', 'desc')
            .limit(20)
            .execute();

        const fileIds = publicTracks.map(f => f.file_id);
        const fileAttributes = fileIds.length === 0 ? [] : await db.selectFrom('Attribute')
            .select(['parent_id', 'name', 'value'])
            .where('parent_type', '=', 'File')
            .where('parent_id', 'in', fileIds)
            .where('name', 'in', ['duration', 'description'])
            .execute();

        const userAttributes = await db.selectFrom('Attribute')
            .select(['name', 'value'])
            .where('parent_type', '=', 'User')
            .where('parent_id', '=', args.user_id)
            .where('name', 'in', ['bio', 'social_links'])
            .execute();

        const bio = userAttributes.find(a => a.name === 'bio')?.value || null;
        const socialLinksRaw = userAttributes.find(a => a.name === 'social_links')?.value;
        const socialLinks = socialLinksRaw ? (JSON.parse(socialLinksRaw)?.links || []) : [];

        const tracksWithAttributes = publicTracks.map(track => ({
            id: track.file_id,
            name: track.name,
            relative_path: track.relative_path,
            mime_type: track.mime_type,
            size: track.size,
            earliest_known_modified_at: track.earliest_known_modified_at,
            workspace_id: track.workspace_id,
            created_by: track.created_by,
            created_at: track.created_at,
            updated_at: track.updated_at,
            deleted_at: track.deleted_at,
            versioning_mode: track.versioning_mode,
            auto_version: track.auto_version,
            file_revision_id: track.file_revision_id,
            revision_num: track.revision_num,
            published_at: track.published_at,
            creator_name: track.creator_name,
            is_own_track: track.created_by === args.user_id,
            attributes: {
                duration: fileAttributes.find(a => a.parent_id === track.file_id && a.name === 'duration')?.value,
                description: fileAttributes.find(a => a.parent_id === track.file_id && a.name === 'description')?.value,
            }
        }));

        return {
            user,
            publicFiles: tracksWithAttributes,
            bio,
            socialLinks,
        };
    });

    public getUserProfileDataAuthenticated = createZodAction(z.object({
        user_id: z.string(),
    }), async (args) => {
        const db = this.getDB();

        const currentUserContext = await getCurrentUserContext(this.moduleAPI);
        if (!currentUserContext) {
            throw new Error('Not logged in');
        }

        const user = await db.selectFrom('User')
            .select(['id', 'name', 'created_at', 'updated_at', 'profile_picture_file_id'])
            .where('id', '=', args.user_id)
            .executeTakeFirst();

        if (!user) {
            return null;
        }

        const mutualWorkspaces = await db.selectFrom('WorkspaceMember as wm1')
            .innerJoin('WorkspaceMember as wm2', 'wm1.workspace_id', 'wm2.workspace_id')
            .innerJoin('Workspace', 'Workspace.id', 'wm1.workspace_id')
            .select(['Workspace.id', 'Workspace.name'])
            .where('wm1.user_id', '=', currentUserContext.id)
            .where('wm2.user_id', '=', args.user_id)
            .execute();

        if (mutualWorkspaces.length === 0) {
            return null;
        }

        const workspaceIds = mutualWorkspaces.map(w => w.id);

        const recentProjectRevisions = await db.selectFrom('ProjectRevision')
            .innerJoin('Project', 'Project.id', 'ProjectRevision.project_id')
            .where('ProjectRevision.created_by', '=', args.user_id)
            .where('Project.workspace_id', 'in', workspaceIds)
            .select([
                'ProjectRevision.id',
                'ProjectRevision.project_id',
                'ProjectRevision.created_at',
                'Project.name as project_name',
                'Project.workspace_id',
            ])
            .orderBy('ProjectRevision.created_at', 'desc')
            .limit(10)
            .execute();

        return {
            user,
            mutualWorkspaces,
            recentProjectRevisions,
        };
    });

    public getPublicFileData = createZodAction(z.object({
        file_id: z.string(),
    }), async (args) => {
        const db = this.getDB();

        // TODO: Add is_public column to File table
        const file = await db.selectFrom('File')
            .selectAll()
            .where('id', '=', args.file_id)
            // .where('is_public', '=', 1)
            // .where('deleted_at', 'is', null)
            .executeTakeFirst();

        if (!file) {
            return null;
        }

        const attributes = await db.selectFrom('Attribute')
            .select(['name', 'value'])
            .where('parent_type', '=', 'File')
            .where('parent_id', '=', args.file_id)
            .where('name', 'in', ['duration', 'description'])
            .execute();

        const creator = await db.selectFrom('User')
            .select(['id', 'name'])
            .where('id', '=', file.created_by)
            .executeTakeFirst();

        return {
            ...file,
            attributes: {
                duration: attributes.find(a => a.name === 'duration')?.value,
                description: attributes.find(a => a.name === 'description')?.value,
            },
            creator,
        };
    });

    public getTrimJobStatus = createZodAction(z.object({
        job_id: z.string().min(1),
        workspace_id: z.string().min(1),
    }), async (args) => {
        const db = this.getDB();

        

        const jobStatus = await trimService.getJobStatus(args.job_id, 5000);

        if (!jobStatus.success) {
            return {
                status: 'failed' as const,
                error: jobStatus.error
            };
        }

        if (!jobStatus.outputPath) {
            return {
                status: 'processing' as const,
                jobId: args.job_id
            };
        }

        let newFileId: string | undefined;
        const fileName = jobStatus.outputPath.split('/').pop() || jobStatus.outputPath;

        const recentFile = await db.selectFrom('File')
            .select('id')
            .where('name', '=', fileName)
            .where('workspace_id', '=', args.workspace_id)
            .orderBy('created_at', 'desc')
            .executeTakeFirst();

        if (recentFile) {
            newFileId = recentFile.id;
        }

        return {
            status: 'completed' as const,
            jobId: args.job_id,
            outputPath: jobStatus.outputPath,
            fileSize: jobStatus.fileSize,
            newFileId,
        };
    });
}

export type NestedComment = {
    id: string;
    text: string;
    created_by: string;
    created_at: string;
    replies: NestedComment[];
    workspace_id: string;
};

export const getCurrentUserContext = async (moduleAPI: ModuleAPI) => {
    const userContext = moduleAPI.deps.module.moduleRegistry.getModule('UserContext').getUserContext();
    if (userContext.type === 'remote') {
        return userContext;
    }

    if (userContext.type === 'local') {
        const users = await moduleAPI.deps.module.moduleRegistry.getModule('Kysely').db().selectFrom('User').select('id').execute();

        if (users.length === 0) {
            throw new Error('No user found');
        }

        if (users.length > 1) {
            throw new Error('More than one user found');
        }

        return {
            id: users[0].id,
            type: 'local',
        } as const;
    }

    throw new Error('User not logged in');
};

export const checkWorkspaceMembership = async (moduleAPI: ModuleAPI, args: {workspace_id: string}) => {
    if (!args?.workspace_id) {
        return false;
    }

    const userContext = await getCurrentUserContext(moduleAPI);
    if (!userContext) {
        return false;
    }

    if (args.workspace_id === userContext.id) {
        return true;
    }

    const db = moduleAPI.deps.module.moduleRegistry.getModule('Kysely').db();

    const workspaceMemberId = await db.selectFrom('WorkspaceMember')
        .select('WorkspaceMember.id')
        .where('WorkspaceMember.workspace_id', '=', args.workspace_id)
        .where('WorkspaceMember.user_id', '=', userContext.id)
        .executeTakeFirst();

    return workspaceMemberId;
};

export const getModeForWorkspaceId = (workspaceModule: AllModules['Workspaces'], args: {workspace_id: string}) => {
    const knownWorkspaces = workspaceModule.knownWorkspacesState.getState();

    const matchingWorkspace = knownWorkspaces.find(w => w.workspace.id === args.workspace_id);
    const isLocalWorkspace = Boolean(matchingWorkspace && matchingWorkspace.type === 'local');

    const mode = isLocalWorkspace ? 'local' : 'remote';
    return mode;
};

const getFilesForProjects = async (db: Kysely<DB>, projectIds: string[]) => {
    const files = await db.selectFrom('File')
        .selectAll('File')
        .innerJoin('Attachment', 'Attachment.child_id', 'File.id')
        .innerJoin('Project', 'Attachment.parent_id', 'Project.id')
        .leftJoin('Attribute as DurationAttr', (join) =>
            join
                .onRef('DurationAttr.parent_id', '=', 'File.id')
                .on('DurationAttr.parent_type', '=', 'File')
                .on('DurationAttr.name', '=', 'duration')
        )
        .select('Project.id as project_id')
        .select('DurationAttr.value as duration')
        .where('Attachment.parent_type', '=', 'Project')
        .where('Attachment.child_type', '=', 'File')
        .where('Attachment.parent_id', 'in', projectIds)
        .groupBy(['File.id', 'Project.id', 'DurationAttr.value'])
        .execute();

    return files;
};
